import { countLetterInPhrase, getAvailableActions, GameState, VOWEL_COST } from './index';

export type JuiceCue = 'spin' | 'tick' | 'cash' | 'hit' | 'miss' | 'bankrupt' | 'lose' | 'solve' | 'win' | 'click';

export const HOW_TO_STORAGE_KEY = 'spin-solve-howto-seen';

export const HOW_TO_STEPS = [
  {
    title: 'Spin for cash',
    body: 'On your turn, pull the lever. The number is what each matching consonant is worth — not instant cash.',
  },
  {
    title: 'Tap a consonant',
    body: 'Hits pay that spin value and you keep your turn. Misses pass it. Build a streak, then solve.',
  },
  {
    title: 'Vowels & solve',
    body: 'Buy A E I O U for $250 from this round’s money. Solve the puzzle to bank what you have.',
  },
  {
    title: 'Win the night',
    body: 'BANKRUPT wipes round money only — banked cash is safe. Highest bank after the last round wins.',
  },
  {
    title: 'Table Challenge',
    body: 'Hit TABLE (the 21 reel), or tap Push it? after a $1,500+ spin, for a one-hit Table 21. Win doubles this spin. Lose the spin — round money stays.',
  },
] as const;

export const VOWEL_KEYS = ['a', 'e', 'i', 'o', 'u'] as const;
export const CONSONANT_ROWS: string[][] = [
  ['b', 'c', 'd', 'f', 'g', 'h', 'j'],
  ['k', 'l', 'm', 'n', 'p', 'q', 'r'],
  ['s', 't', 'v', 'w', 'x', 'y', 'z'],
];

export const SOLVE_ROWS: string[][] = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
];

export function formatMoney(amount: number): string {
  return `$${Math.max(0, Math.round(amount)).toLocaleString('en-US')}`;
}

export function specialReelLabel(special: string | null | undefined): string {
  if (special === 'LOSE TURN') return 'PASS';
  if (special === 'BANKRUPT') return 'BUST';
  if (special === 'TABLE') return '21';
  if (!special || special === '$$$') return 'CASH';
  return special;
}

export function spinResultLabel(
  spinValue: number | string,
  specialValue: string | null | undefined,
  options: { spinning?: boolean; canPull?: boolean } = {}
): string {
  if (options.spinning) return 'Spinning…';
  if (typeof spinValue === 'number' && spinValue > 0) return formatMoney(spinValue);
  if (typeof spinValue === 'string' && spinValue) return specialReelLabel(spinValue);
  if (specialValue && specialValue !== '$$$') return specialReelLabel(specialValue);
  return options.canPull ? 'Pull to spin' : '';
}

export function isBadSpinLabel(label: string): boolean {
  return label === 'BUST' || label === 'PASS' || label === 'BANKRUPT' || label === 'LOSE TURN';
}

export function phraseWords(phrase: string): string[] {
  return phrase.trim().split(/\s+/).filter(Boolean);
}

export function computeTileSize(phrase: string, maxWidth: number, maxHeight?: number): number {
  const words = phraseWords(phrase);
  const longest = Math.max(1, ...words.map((word) => word.length));
  const gap = 4;
  let size = Math.floor((maxWidth - (longest - 1) * gap) / longest);
  size = Math.max(20, Math.min(48, size));
  if (maxHeight && maxHeight > 48) {
    const rows = Math.max(1, words.length);
    const vGap = 6;
    const chrome = 22;
    const byHeight = Math.floor((maxHeight - chrome - (rows - 1) * vGap) / rows / 1.25);
    size = Math.max(18, Math.min(size, byHeight));
  }
  return size;
}

export type PromptTone = 'you' | 'wait' | 'round' | 'over';

export interface ActionPrompt {
  headline: string;
  detail: string;
  tone: PromptTone;
}

export function actionPrompt(state: GameState, playerId: string): ActionPrompt {
  const actions = getAvailableActions(state, playerId);
  const current = state.players[state.currentPlayerIndex];
  const name = current?.name || 'Someone';

  if (state.status === 'finished' || state.phase === 'game_over') {
    return { headline: 'Game over', detail: state.message, tone: 'over' };
  }
  if (state.phase === 'round_complete') {
    return { headline: 'Round complete', detail: state.message, tone: 'round' };
  }
  if (state.phase === 'awaiting_table_choice' || state.phase === 'awaiting_table') {
    if (!actions.isMyTurn) {
      return {
        headline: `${name} is at Table 21`,
        detail: state.message || 'Cards are out — hang tight.',
        tone: 'wait',
      };
    }
    if (state.phase === 'awaiting_table_choice') {
      return {
        headline: 'Table 21',
        detail: `Pick House or a player for ${formatMoney(state.currentSpinValue ?? 0)}.`,
        tone: 'you',
      };
    }
    return {
      headline: 'Hit or stand',
      detail: `One card max. They stand on 17+. Stake ${formatMoney(
        state.table?.stake ?? state.currentSpinValue ?? 0
      )}.`,
      tone: 'you',
    };
  }
  if (!actions.isMyTurn) {
    return {
      headline: `${name}'s turn`,
      detail: state.message || 'The board updates live — watch the spin.',
      tone: 'wait',
    };
  }
  if (actions.canGuessConsonant && state.currentSpinValue != null) {
    return {
      headline: 'Pick a consonant',
      detail: actions.canPushIt
        ? `${formatMoney(state.currentSpinValue)} each. Hits keep your turn. Or Push it? at Table 21.`
        : `${formatMoney(state.currentSpinValue)} each. Hits keep your turn.`,
      tone: 'you',
    };
  }
  if (actions.canBuyVowel) {
    return {
      headline: 'Spin, vowel, or solve',
      detail: `Keep a streak going, buy a vowel for ${formatMoney(VOWEL_COST)}, or take the puzzle.`,
      tone: 'you',
    };
  }
  return {
    headline: 'Spin or solve',
    detail: 'Pull for cash, then call a consonant. Solve whenever you know it.',
    tone: 'you',
  };
}

export function playerDisplayName(user: { displayName: string | null; email: string | null } | null): string {
  if (!user) return 'Player';
  if (user.displayName?.trim()) return user.displayName.trim();
  const email = user.email || '';
  return email.split('@')[0] || 'Player';
}

export type OverlayKind = 'solve' | 'bankrupt' | 'win' | 'pass' | 'hit' | null;

export function juiceEventForTransition(prev: GameState | null, next: GameState): JuiceCue | null {
  if (!prev) return null;
  if ((next.status === 'finished' || next.phase === 'game_over') && prev.status !== 'finished') {
    return 'win';
  }
  if (next.phase === 'round_complete' && prev.phase !== 'round_complete') {
    return 'solve';
  }
  if (next.guessedLetters.length > prev.guessedLetters.length) {
    const letter = next.guessedLetters[next.guessedLetters.length - 1];
    return countLetterInPhrase(next.currentPhrase, letter) > 0 ? 'hit' : 'miss';
  }
  if (next.table?.outcome && next.table.outcome !== prev.table?.outcome) {
    if (next.table.outcome === 'win') return 'cash';
    if (next.table.outcome === 'lose') return 'lose';
    return 'tick';
  }
  if (next.lastSpinAt && next.lastSpinAt !== prev.lastSpinAt && next.lastSpin) {
    if (next.lastSpin.specialValue === 'BANKRUPT') return 'bankrupt';
    if (next.lastSpin.specialValue === 'LOSE TURN') return 'lose';
    return 'cash';
  }
  return null;
}

export function overlayKindForTransition(prev: GameState | null, next: GameState): OverlayKind {
  if (next.table?.outcome && next.table.outcome !== prev?.table?.outcome) {
    return null;
  }
  const cue = juiceEventForTransition(prev, next);
  if (cue === 'win' || cue === 'solve' || cue === 'bankrupt') {
    return cue;
  }
  if (cue === 'lose') return 'pass';
  return null;
}

export function spinLandCue(specialValue: string | null | undefined): JuiceCue {
  if (specialValue === 'BANKRUPT') return 'bankrupt';
  if (specialValue === 'LOSE TURN') return 'lose';
  if (specialValue === 'TABLE') return 'cash';
  return 'cash';
}

export function overlayFromSpin(specialValue: string | null | undefined): OverlayKind {
  if (specialValue === 'BANKRUPT') return 'bankrupt';
  if (specialValue === 'LOSE TURN') return 'pass';
  return null;
}
